import './ProfileForm.scss'
import { getLocales } from '../../../../utilities/locales'
import Loader from '../../../../widgets/Generic/Loader'
import React, { useState, useEffect, useRef } from 'react'
import Toast from '../../../Toastr/Toast'
import translate from '../../../../utilities/translate'
import translations from './translations'
import { getAlphabeticallySortedCountriesByLocale } from '../../utils'
import Form from '../../../../widgets/Form'
import Input from '../../../../widgets/Form/Input'
import Select from '../../../../widgets/Form/Select'
import Error from '../../../../widgets/Form/Error'
import SecondaryButton from '../../../../widgets/Button/SecondaryButton'
import CancelButton from '../../../../widgets/Button/CancelButton'
import SubmitButton from '../../../../widgets/Button/SubmitButton'
import CloseIcon from '../../../Icons/CloseIcon'
import { isNil } from 'ramda'
import { isDefined, isEmailValid } from '../../../../utilities/strings'
import useFocusInvalidInput from '../../../../hooks/useFocusInvalidInput'

// trans :: String -> String -> String
const trans = translate(translations)

export default ({
  error,
  locale,
  submit,
  submitting,
  success,
  user,
}) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const ref = useRef(null)

  const [gender, setGender] = useState('')
  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [nickname, setNickname] = useState('')
  const [email, setEmail] = useState('')
  const [repeatEmail, setRepeatEmail] = useState('')
  const [country, setCountry] = useState('')
  const [language, setLanguage] = useState('')

  const [nicknameError, setNicknameError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [repeatEmailError, setRepeatEmailError] = useState('')
  const [countryError, setCountryError] = useState('')
  const [languageError, setLanguageError] = useState('')

  const [formErrors, setFormErrors] = useState({})

  useFocusInvalidInput(formErrors)

  useEffect(() => {
    if (isEditMode && ref && ref.current) {
      ref.current.focus()
    }
  }, [isEditMode])

  useEffect(() => {
    setGender(user.gender ?? '')
    setFirstname(user.firstname ?? '')
    setLastname(user.lastname ?? '')
    setNickname(user.nickname ?? '')
    setEmail(user.email ?? '')
    setCountry(user.country ?? '')
    setLanguage(user.locale ?? '')
  }, [user, isEditMode])

  const handleSubmit = () => {
    const completeEmailError = emailError === '' && !isEmailValid(email) ? trans(locale)('errors.invalidEmail') : emailError
    const completeRepeatEmailError = (user.email !== email && email !== repeatEmail)? trans(locale)('errors.emailMismatch') : repeatEmailError

    if (
      completeEmailError !== '' ||
      completeRepeatEmailError !== '' ||
      nicknameError !== '' ||
      countryError !== '' ||
      languageError !== ''
    ) {
      setFormErrors({
        nickname: nicknameError,
        email: completeEmailError,
        repeatEmail: completeRepeatEmailError,
        country: countryError,
        language: languageError,
      })

      return
    }

    setFormErrors({})
    submit(
      isDefined(gender) ? gender : null,
      isDefined(firstname) ? firstname : null,
      isDefined(lastname) ? lastname : null,
      nickname,
      country,
      language,
      email,
    )
  }

  const cancelEmailEdit = () => {
    setEmail(user.email ?? '')
    setRepeatEmail('')
  }

  return (
    <Form noValidate={ true } onSubmit={ handleSubmit }>
      <div className="names-wrapper columns">
        <div className="form-group column col-2 col-xs-12">
          <Select
            ref={ ref }
            label={ trans(locale)('gender') }
            name="gender"
            value={ gender }
            onChange={ setGender }
            disabled={ !isEditMode }
          >
            <Select.Option value=""></Select.Option>
            <Select.Option value="Mr">{ trans(locale)('mr') }</Select.Option>
            <Select.Option value="Mrs">{ trans(locale)('mrs') }</Select.Option>
            <Select.Option value="Miss">{ trans(locale)('miss') }</Select.Option>
          </Select>
        </div>
        <div className="form-group column col-5 col-xs-6">
          <Input
            label={ trans(locale)('firstname') }
            name="firstname"
            value={ firstname }
            onChange={ setFirstname }
            disabled={ !isEditMode }
          />
        </div>
        <div className="form-group column col-5 col-xs-6">
          <Input
            label={ trans(locale)('lastname') }
            name="lastname"
            value={ lastname }
            onChange={ setLastname }
            disabled={ !isEditMode }
          />
        </div>
      </div>

      <Input
        locale={ locale }
        label={ trans(locale)('nickname') }
        name="nickname"
        value={ nickname }
        onChange={ setNickname }
        error={ formErrors.nickname }
        onError={ setNicknameError }
        disabled={ !isEditMode }
        required={ true }
      />

      <Input
        locale={ locale }
        label={ trans(locale)('email') }
        name="email"
        type="email"
        value={ email }
        onChange={ setEmail }
        onError={ setEmailError }
        error={ formErrors.email }
        disabled={ !isEditMode }
        required={ true }
      />

      { isEditMode && email !== user.email
        ? <div className={ `email-confirm-wrapper ${!isNil(formErrors.repeatEmail) ? 'is-error' : ''}` }>
          <Input
            locale={ locale }
            label={ trans(locale)('confirm') }
            name="repeatEmail"
            type="email"
            value={ repeatEmail }
            onChange={ setRepeatEmail }
            onError={ setRepeatEmailError }
            error={ formErrors.repeatEmail }
            required={ true }
          />
          <div>
            <CancelButton onClick={ cancelEmailEdit } aria-label={ trans(locale)('cancelEmailEdit') }>
              <CloseIcon width={ 15.5 } height={ 15.5 } />
            </CancelButton>
          </div>
        </div>
        : null
      }

      <div className="country-and-language columns">
        <div className="form-group column">
          <Select
            locale={ locale }
            label={ trans(locale)('country') }
            name="country"
            value={ country }
            onChange={ setCountry }
            error={ formErrors.country }
            onError={ setCountryError }
            disabled={ !isEditMode }
            required={ true }
          >
            <Select.Option value=""></Select.Option>
            { getAlphabeticallySortedCountriesByLocale(locale).map(country =>
              <Select.Option key={ country.code } value={ country.code }>
                {country.name}
              </Select.Option>,
            ) }
          </Select>
        </div>

        <div className="form-group column">
          <Select
            label={ trans(locale)('language') }
            name="language"
            value={ language }
            onChange={ setLanguage }
            error={ formErrors.language }
            onError={ setLanguageError }
            disabled={ !isEditMode }
            required={ true }
          >
            <Select.Option value=""></Select.Option>
            { getLocales().map(lc =>
              <Select.Option key={ lc } value={ lc }>
                {trans(locale)(`languages.${lc}`)}
              </Select.Option>,
            ) }
          </Select>
        </div>
      </div>

      <p className="mandatory-description">
        { trans(locale)('mandatoryFields') }
      </p>
      { error ? <Error>{ trans(locale)('error') }</Error> : null }

      <div className="buttons-wrapper">
        { isEditMode
          ? <>
            <SubmitButton disabled={ submitting }>
              { !submitting
                ? trans(locale)('save')
                : <Loader size="small" color="white" />
              }
            </SubmitButton>
            <CancelButton onClick={ () => setIsEditMode(false) }>{trans(locale)('cancel')}</CancelButton>
          </>
          : <SecondaryButton onClick={ () => setIsEditMode(true) }>{ trans(locale)('editProfile') }</SecondaryButton>
        }
      </div>

      { success ? <Toast message={ trans(locale)('success') } level="success" /> : null }
    </Form>
  )
}
